/**
 * Vault error types, in their OWN file with ZERO imports.
 *
 * Same reason `studentPiiFields.ts` stands alone: these lived in `vaultStore.ts`, which imports the Firebase
 * SDK, so a test that wanted to assert "this throws VaultKeysCorruptError" had to boot Firebase to reference
 * the class — and in a node test environment that fails on an invalid API key before any assertion runs.
 *
 * An error type should not require a database client to name.
 */

/** A write that would have overwritten a newer record. The caller re-reads and retries. */
export class VaultStaleWriteError extends Error {
  constructor(
    public readonly studentId: string,
    public readonly storedRev: number,
    public readonly attemptedRev: number,
  ) {
    super(
      `Refusing to overwrite a newer vault record for ${studentId}: stored revision ${storedRev}, `
      + `attempted ${attemptedRev}. Another device has edited this student since this one last read.`,
    );
    this.name = 'VaultStaleWriteError';
  }
}

/**
 * The keys document holds wraps but not the parameters needed to derive the key that opens them.
 *
 * Unrecoverable automatically — the salt is gone — so this must stop the flow rather than be smoothed over.
 * Smoothing it over means treating the account as fresh, which re-keys every class on it.
 */
export class VaultKeysCorruptError extends Error {
  constructor(uid: string) {
    super(`The vault keys for ${uid} list classes but have no KDF parameters, so the key that opens them `
      + 'cannot be derived. Setting up a new vault would overwrite the existing wraps. This needs looking at '
      + 'rather than retrying.');
    this.name = 'VaultKeysCorruptError';
  }
}

/**
 * Another device created a wrap for this class while this one was setting it up.
 *
 * ── WHY THIS HAS TO BE AN ERROR AND NOT A MERGE ─────────────────────────────────────────────────
 *
 * The wrap write was a merging `setDoc`, so two tabs that both read "no vault for this class" both wrote,
 * and the last one won. The loser kept a DEK in memory and in its IndexedDB cache, and had already SHOWN
 * the teacher a recovery code, none of which corresponds to any stored wrap.
 *
 * That is survivable right up until the loser runs a migration: it seals every record under its own key,
 * verifies by opening them with that same session key — which of course succeeds — and then deletes the
 * plaintext. From any other browser the passphrase now opens nothing and the written-down recovery code
 * is rejected. Every child's name is gone, permanently, with no error anywhere along the way.
 *
 * The trigger is ordinary: a teacher with the gradebook open in two tabs, or a laptop and a phone, on the
 * first day a "Protect this class" banner appears on every class.
 *
 * So the loser must find out. The caller's correct response is to discard what it generated and adopt the
 * stored wrap instead — the same thing it would have done had it seen the wrap in the first place.
 */
export class VaultWrapRaceError extends Error {
  constructor(public readonly classId: string) {
    super(
      `Another device finished setting up the vault for class ${classId} first. Discarding this device's `
      + 'key and adopting the stored one.',
    );
    this.name = 'VaultWrapRaceError';
  }
}

/**
 * The passphrase produced a key that unwrapped cleanly and then opened no records at all.
 *
 * ── WHY THIS IS NOT "WRONG PASSPHRASE" ──────────────────────────────────────────────────────────
 *
 * A wrong passphrase fails at the unwrap: AES-GCM's tag check rejects it, and the teacher is told to try
 * again. This is different and worse. The key is the one the stored wrap yields, and it still matches
 * nothing in the roster — which means the records were sealed under a DIFFERENT key, and no wrap on the
 * account reproduces it.
 *
 * The usual cause is a concurrent setup from two tabs, where one device's key won the keys document while
 * the other device's key sealed the records. That race is now prevented, but a vault created before the
 * fix can still be in this state, and so can one whose keys document was restored from a backup.
 *
 * ── WHY IT THROWS ───────────────────────────────────────────────────────────────────────────────
 *
 * Because it used to be returned, and every caller discarded it. `unlockWithSecret` handed back
 * `{opened: 0, failed: [...]}`, the context dropped it, the modal called its success handler, and the
 * teacher saw "Unlocked on this device" with a green banner and every name still a placeholder. The one
 * state that most needs a human to look at it was the one that looked fine.
 */
export class VaultKeyOpensNothingError extends Error {
  constructor(public readonly failed: readonly string[]) {
    super(
      `That passphrase is correct, but it does not open any of this class's ${failed.length} encrypted `
      + 'records — they were saved with a different key. Nothing has been lost from our side, but we '
      + 'cannot read them and neither can this device. If you set this class up on two devices at once, '
      + 'try the recovery code from the other one.',
    );
    this.name = 'VaultKeyOpensNothingError';
  }
}

/**
 * The teacher's keys document changed while a whole-document rewrite was being prepared.
 *
 * `changePassphrase` and `resetPassphraseWithRecoveryCodes` replace every class wrap at once, and they
 * spend seconds deriving keys between reading the document and writing it. A class protected on another
 * device inside that window used to be ERASED by the write — its passphrase wrap and its recovery wrap
 * both, with the other device's cached key the only remaining copy of its DEK and no error on either
 * side.
 *
 * The caller's correct response is to re-read and run again: it costs one derivation and loses nothing.
 */
export class VaultKeysMovedError extends Error {
  constructor(public readonly addedClassIds: readonly string[]) {
    super(
      addedClassIds.length
        ? `Another device protected ${addedClassIds.length} class(es) while this change was being `
          + 'prepared. Nothing was written — try again.'
        : 'The passphrase was changed on another device while this change was being prepared. '
          + 'Nothing was written — try again.',
    );
    this.name = 'VaultKeysMovedError';
  }
}
